import { createStore, type StoreApi } from "zustand/vanilla";
import { useStore } from "zustand";
import { api as realApi } from "./http";
import { realtime as realRt, type Realtime } from "./realtime";
import type { Frame, ConversationView, DirUser, MemberView, PresenceState, ServerMessage } from "./types";

export interface LocalMsg extends ServerMessage {
  sendState?: "sending" | "failed";
}

export interface ConvEntry {
  view: ConversationView;
  partnerId: string;
  /** how far the other person has read (DMs) */
  partnerRead: number;
}

export interface ChatState {
  meId: string;
  meName: string;
  loaded: boolean;
  error: string;
  users: Record<string, DirUser>;
  convs: Record<string, ConvEntry>;
  dmOf: Record<string, string>;
  msgs: Record<string, LocalMsg[]>;
  hasMore: Record<string, boolean>;
  presence: Record<string, PresenceState>;
  typing: Record<string, Record<string, number>>;
  activeConv: string | null;
  windowOpen: boolean;
}

export interface ChatDeps {
  api: typeof realApi;
  rt: Pick<Realtime, "request" | "notify" | "isOpen" | "onFrame" | "onState">;
  now?: () => number;
  uuid?: () => string;
  visible?: () => boolean;
}

const TYPING_TTL_MS = 5000;
const TYPING_SEND_EVERY_MS = 3000;

const initial = (): ChatState => ({
  meId: "",
  meName: "",
  loaded: false,
  error: "",
  users: {},
  convs: {},
  dmOf: {},
  msgs: {},
  hasMore: {},
  presence: {},
  typing: {},
  activeConv: null,
  windowOpen: false,
});

/** Messages of one conversation, oldest first. Confirmed rows are ordered by seq; unsent rows stay at the end. */
export const mergeMessage = (list: LocalMsg[], m: LocalMsg): LocalMsg[] => {
  const out = list.slice();
  let i = out.findIndex((x) => x.id === m.id);
  if (i < 0 && m.clientMsgId) i = out.findIndex((x) => x.clientMsgId === m.clientMsgId);
  if (i >= 0) {
    const merged: LocalMsg = { ...out[i]!, ...m };
    if (!m.sendState) delete merged.sendState; // a confirmed copy ends the "sending" / "failed" state
    out[i] = merged;
  } else out.push(m);
  return out.sort((a, b) => {
    const as = a.sendState ? Infinity : a.seq;
    const bs = b.sendState ? Infinity : b.seq;
    if (as !== bs) return as - bs;
    return Date.parse(a.createdAt) - Date.parse(b.createdAt);
  });
};

export const maxSeq = (list: LocalMsg[] | undefined): number => (list ?? []).reduce((n, m) => (m.sendState ? n : Math.max(n, m.seq)), 0);

export function createChatStore(deps: ChatDeps): { store: StoreApi<ChatState>; actions: ChatActions } {
  const store = createStore<ChatState>(() => initial());
  const now = deps.now ?? Date.now;
  const uuid = deps.uuid ?? (() => crypto.randomUUID());
  const visible = deps.visible ?? (() => typeof document === "undefined" || document.visibilityState === "visible");
  const set = store.setState;
  const get = store.getState;
  const lastTypingSent: Record<string, number> = {};
  const loadingMembers = new Set<string>();

  const upsertUsers = (us: DirUser[]) =>
    set((s) => {
      const users = { ...s.users };
      us.forEach((u) => (users[u.id] = { ...users[u.id], ...u }));
      return { users };
    });

  const setPresence = (ps: PresenceState[]) =>
    set((s) => {
      const presence = { ...s.presence };
      ps.forEach((p) => p.userId && (presence[p.userId] = p));
      return { presence };
    });

  const watch = async (ids: string[]) => {
    const want = ids.filter((id) => id && id !== get().meId);
    if (want.length === 0) return;
    try {
      const r = await deps.rt.request<{ presence: PresenceState[] }>("presence.watch", { users: want.slice(0, 200) });
      setPresence(r.presence ?? []);
    } catch {
      try {
        const r = await deps.api<{ presence: PresenceState[] }>("/api/chat/presence", { method: "POST", body: { users: want.slice(0, 200) } });
        setPresence(r.presence ?? []);
      } catch {
        /* presence is a nicety: stay silent */
      }
    }
  };

  /** For a DM we need the other person's id and how far they have read. */
  const loadMembers = async (convId: string) => {
    if (loadingMembers.has(convId)) return;
    loadingMembers.add(convId);
    try {
      const r = await deps.api<{ members: MemberView[] }>(`/api/chat/conversations/${encodeURIComponent(convId)}/members`);
      const other = r.members.find((m) => m.userId !== get().meId);
      if (!other) return;
      upsertUsers([{ id: other.userId, name: other.name, email: other.email ?? "" }]);
      set((s) => {
        const e = s.convs[convId];
        if (!e) return s;
        return {
          convs: { ...s.convs, [convId]: { ...e, partnerId: other.userId, partnerRead: Math.max(e.partnerRead, other.lastReadSeq) } },
          dmOf: { ...s.dmOf, [other.userId]: convId },
        };
      });
      void watch([other.userId]);
    } catch {
      /* retried the next time the conversation is opened */
    } finally {
      loadingMembers.delete(convId);
    }
  };

  const addConv = (v: ConversationView) => {
    set((s) => {
      const prev = s.convs[v.id];
      const entry: ConvEntry = { view: v, partnerId: prev?.partnerId ?? "", partnerRead: prev?.partnerRead ?? 0 };
      return { convs: { ...s.convs, [v.id]: entry } };
    });
    if (v.kind === "dm" && !get().convs[v.id]?.partnerId) void loadMembers(v.id);
  };

  const patchView = (convId: string, patch: Partial<ConversationView>) =>
    set((s) => {
      const e = s.convs[convId];
      return e ? { convs: { ...s.convs, [convId]: { ...e, view: { ...e.view, ...patch } } } } : s;
    });

  const isLooking = (convId: string) => get().windowOpen && get().activeConv === convId && visible();

  const putMessage = (m: LocalMsg) => set((s) => ({ msgs: { ...s.msgs, [m.convId]: mergeMessage(s.msgs[m.convId] ?? [], m) } }));

  const actions: ChatActions = {
    init(meId, meName) {
      set({ ...initial(), meId, meName });
      const cleanup = setInterval(() => {
        const t = now();
        set((s) => {
          let changed = false;
          const typing: ChatState["typing"] = {};
          for (const [c, users] of Object.entries(s.typing)) {
            const keep = Object.fromEntries(Object.entries(users).filter(([, until]) => until > t));
            if (Object.keys(keep).length !== Object.keys(users).length) changed = true;
            if (Object.keys(keep).length) typing[c] = keep;
          }
          return changed ? { typing } : s;
        });
      }, 1500);
      const offFrame = deps.rt.onFrame((f) => actions.handleFrame(f));
      const offState = deps.rt.onState((st, reopened) => {
        if (st === "open") void actions.resync(reopened);
      });
      return () => {
        clearInterval(cleanup);
        offFrame();
        offState();
      };
    },

    async resync(reopened) {
      try {
        const r = await deps.api<{ conversations: ConversationView[] }>("/api/chat/conversations");
        const convs: ChatState["convs"] = {};
        r.conversations
          .filter((c) => c.joined !== false)
          .forEach((v) => {
            const prev = get().convs[v.id];
            convs[v.id] = { view: v, partnerId: prev?.partnerId ?? "", partnerRead: prev?.partnerRead ?? 0 };
          });
        set({ convs, loaded: true, error: "" });
        r.conversations.filter((c) => c.kind === "dm").forEach((c) => !convs[c.id]?.partnerId && void loadMembers(c.id));
        // fetch what was missed while offline
        if (reopened) {
          const cursors: Record<string, number> = {};
          Object.keys(get().msgs).forEach((id) => {
            const n = maxSeq(get().msgs[id]);
            if (n > 0 && convs[id]) cursors[id] = n;
          });
          if (Object.keys(cursors).length) {
            const sync = await deps.rt.request<{ conversations: Record<string, { messages: ServerMessage[] }> }>("sync.all", { cursors, limit: 100 });
            Object.values(sync.conversations ?? {}).forEach((p) => p.messages.forEach((m) => putMessage(m)));
          }
          // refresh the newest page of the open conversation so edits and deletions made meanwhile show up
          const open = get().activeConv;
          if (open) await actions.loadHistory(open, true);
        }
        void watch(Object.values(get().convs).map((c) => c.partnerId));
        void actions.loadPreviews();
      } catch (e) {
        set({ error: e instanceof Error ? e.message : "Could not load conversations" });
      }
    },

    /** The conversation list shows the last message of each chat; the list call only has counters, so fetch one page for
     *  chats we have nothing from yet, a few at a time. */
    async loadPreviews() {
      const todo = Object.values(get().convs)
        .filter((c) => c.view.kind === "dm" && c.view.lastSeq > 0 && !(get().msgs[c.view.id]?.length))
        .map((c) => c.view.id);
      for (let i = 0; i < todo.length; i += 4) {
        await Promise.all(
          todo.slice(i, i + 4).map((id) =>
            deps
              .api<{ messages: ServerMessage[] }>(`/api/chat/conversations/${encodeURIComponent(id)}/messages`, { query: { limit: 3 } })
              .then((r) => r.messages.forEach((m) => putMessage(m)))
              .catch(() => undefined),
          ),
        );
      }
    },

    async loadDirectory(q = "") {
      try {
        const r = await deps.api<{ users: DirUser[] }>("/api/chat/users", { query: { q, limit: 200 } });
        upsertUsers((r.users ?? []).filter((u) => u.id !== get().meId));
        void watch((r.users ?? []).map((u) => u.id));
      } catch {
        /* the list simply stays as it is */
      }
    },

    async openDM(userId) {
      const known = get().dmOf[userId];
      if (known) return known;
      const r = await deps.api<{ conversation: ConversationView }>("/api/chat/conversations/dm", { method: "POST", body: { userId } });
      const v = r.conversation;
      set((s) => ({
        convs: { ...s.convs, [v.id]: { view: v, partnerId: userId, partnerRead: s.convs[v.id]?.partnerRead ?? 0 } },
        dmOf: { ...s.dmOf, [userId]: v.id },
      }));
      void loadMembers(v.id);
      void watch([userId]);
      return v.id;
    },

    async loadHistory(convId, newest = false) {
      const list = get().msgs[convId] ?? [];
      const before = newest ? 0 : (list.find((m) => !m.sendState)?.seq ?? 0);
      const r = await deps.api<{ messages: ServerMessage[]; hasMore: boolean }>(`/api/chat/conversations/${encodeURIComponent(convId)}/messages`, {
        query: { before: before || undefined, limit: 50 },
      });
      r.messages.forEach((m) => putMessage(m));
      if (!newest || get().hasMore[convId] === undefined) set((s) => ({ hasMore: { ...s.hasMore, [convId]: r.hasMore } }));
    },

    setWindow(open, convId) {
      set({ windowOpen: open, activeConv: convId });
      if (open && convId) void actions.markRead(convId);
    },

    async markRead(convId) {
      const e = get().convs[convId];
      if (!e) return;
      const seq = Math.max(maxSeq(get().msgs[convId]), e.view.lastSeq);
      if (e.view.unread === 0 && e.view.lastReadSeq >= seq) return;
      patchView(convId, { unread: 0, lastReadSeq: Math.max(seq, e.view.lastReadSeq) });
      try {
        if (deps.rt.isOpen()) await deps.rt.request("read", { convId, seq });
        else await deps.api(`/api/chat/conversations/${encodeURIComponent(convId)}/read`, { method: "POST", body: { seq } });
      } catch {
        /* the next open tries again */
      }
    },

    async markAllRead() {
      await Promise.all(Object.keys(get().convs).filter((id) => (get().convs[id]?.view.unread ?? 0) > 0).map((id) => actions.markRead(id)));
    },

    async send(convId, body) {
      const text = body.trim();
      if (!text) return;
      const clientMsgId = uuid();
      const local: LocalMsg = {
        id: "local-" + clientMsgId,
        convId,
        seq: 0,
        senderId: get().meId,
        senderName: get().meName,
        clientMsgId,
        body: text,
        createdAt: new Date(now()).toISOString(),
        sendState: "sending",
      };
      putMessage(local);
      await actions.deliver(convId, clientMsgId);
    },

    async deliver(convId, clientMsgId) {
      const m = (get().msgs[convId] ?? []).find((x) => x.clientMsgId === clientMsgId);
      if (!m) return;
      set((s) => ({ msgs: { ...s.msgs, [convId]: mergeMessage(s.msgs[convId] ?? [], { ...m, sendState: "sending" }) } }));
      try {
        const payload = { clientMsgId, body: m.body };
        let saved: ServerMessage;
        if (deps.rt.isOpen()) {
          saved = (await deps.rt.request<{ message: ServerMessage }>("message.send", { convId, ...payload })).message;
        } else {
          saved = (await deps.api<{ message: ServerMessage }>(`/api/chat/conversations/${encodeURIComponent(convId)}/messages`, { method: "POST", body: payload })).message;
        }
        set((s) => ({
          msgs: { ...s.msgs, [convId]: mergeMessage((s.msgs[convId] ?? []).filter((x) => x.id !== m.id), saved) },
        }));
        const e = get().convs[convId];
        if (e) patchView(convId, { lastSeq: Math.max(e.view.lastSeq, saved.seq), lastReadSeq: Math.max(e.view.lastReadSeq, saved.seq), lastMessageAt: saved.createdAt });
      } catch {
        // the clientMsgId makes a retry safe: the server returns the same message if the first attempt did get through
        set((s) => ({ msgs: { ...s.msgs, [convId]: mergeMessage(s.msgs[convId] ?? [], { ...m, sendState: "failed" }) } }));
      }
    },

    typing(convId) {
      const t = now();
      if (t - (lastTypingSent[convId] ?? 0) < TYPING_SEND_EVERY_MS) return;
      lastTypingSent[convId] = t;
      deps.rt.notify("typing", { convId });
    },

    setStatus(status, text) {
      deps.rt.notify("presence.set", { status, text: text ?? "" });
    },

    handleFrame(f: Frame) {
      const d = f.d as Record<string, unknown>;
      switch (f.t) {
        case "message.new": {
          const m = d as unknown as ServerMessage;
          if (!get().convs[m.convId]) {
            void deps
              .api<{ conversation: ConversationView }>(`/api/chat/conversations/${encodeURIComponent(m.convId)}`)
              .then((r) => {
                addConv(r.conversation);
                putMessage(m);
              })
              .catch(() => undefined);
            return;
          }
          // a reply in a thread does not move the main conversation's unread count
          putMessage(m);
          const e = get().convs[m.convId];
          if (!e) return;
          if (m.senderId === get().meId) {
            patchView(m.convId, { lastSeq: Math.max(e.view.lastSeq, m.seq), lastMessageAt: m.createdAt });
          } else if (isLooking(m.convId)) {
            patchView(m.convId, { lastSeq: Math.max(e.view.lastSeq, m.seq), lastMessageAt: m.createdAt });
            void actions.markRead(m.convId);
          } else if (!m.parentId && !m.system) {
            patchView(m.convId, { unread: e.view.unread + 1, lastSeq: Math.max(e.view.lastSeq, m.seq), lastMessageAt: m.createdAt });
          }
          // they stopped typing the moment the message arrived
          set((s) => {
            const t = s.typing[m.convId];
            if (!t || !(m.senderId in t)) return s;
            const rest = { ...t };
            delete rest[m.senderId];
            return { typing: { ...s.typing, [m.convId]: rest } };
          });
          return;
        }
        case "message.updated":
          putMessage(d as unknown as ServerMessage);
          return;
        case "conversation.new":
          addConv(d as unknown as ConversationView);
          return;
        case "member.removed": {
          const { convId, userId } = d as { convId: string; userId: string };
          if (userId === get().meId)
            set((s) => {
              const convs = { ...s.convs };
              delete convs[convId];
              return { convs };
            });
          return;
        }
        case "typing": {
          const { convId, userId } = d as { convId: string; userId: string };
          if (userId === get().meId) return;
          set((s) => ({ typing: { ...s.typing, [convId]: { ...s.typing[convId], [userId]: now() + TYPING_TTL_MS } } }));
          return;
        }
        case "read": {
          const { convId, userId, seq } = d as { convId: string; userId: string; seq: number };
          const e = get().convs[convId];
          if (!e) return;
          if (userId === get().meId) patchView(convId, { unread: 0, lastReadSeq: Math.max(e.view.lastReadSeq, seq) });
          else set((s) => ({ convs: { ...s.convs, [convId]: { ...e, partnerRead: Math.max(e.partnerRead, seq) } } }));
          return;
        }
        case "presence": {
          const p = d as unknown as PresenceState;
          if (p.userId) setPresence([p]);
          return;
        }
        default:
      }
    },
  };

  return { store, actions };
}

export interface ChatActions {
  init(meId: string, meName: string): () => void;
  resync(reopened: boolean): Promise<void>;
  loadPreviews(): Promise<void>;
  loadDirectory(q?: string): Promise<void>;
  openDM(userId: string): Promise<string>;
  loadHistory(convId: string, newest?: boolean): Promise<void>;
  setWindow(open: boolean, convId: string | null): void;
  markRead(convId: string): Promise<void>;
  markAllRead(): Promise<void>;
  send(convId: string, body: string): Promise<void>;
  deliver(convId: string, clientMsgId: string): Promise<void>;
  typing(convId: string): void;
  setStatus(status: "online" | "away" | "dnd", text?: string): void;
  handleFrame(f: Frame): void;
}

const instance = createChatStore({ api: realApi, rt: realRt });
export const chatStore = instance.store;
export const chat = instance.actions;
export const useChat = <T,>(selector: (s: ChatState) => T): T => useStore(chatStore, selector);
